import React from "react";

import {
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  ToggleButtonGroupProps,
  Typography,
  TypographyProps,
} from "@mui/material";
import styled from "@mui/material/styles/styled";

import type { ParticipantAccessLevel } from "@app/common/types";

import Stack, { Column, StackProps } from "@rui/Stack";
import { ClampedTypography } from "@rui/styles";

import { ACCESS_LEVEL_OPTIONS } from "./constants";

export const DialogTabsContainer = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(0, 2),
  marginTop: theme.spacing(1),
  borderBottom: "2px solid",
  borderColor: theme.palette.shades.neutral[50],
  "& .MuiTabs-root": { marginBottom: "-2px" },
}));

export const DialogTabs = styled(Tabs)({
  minHeight: 0,
  "& .MuiTabs-flexContainer": {
    columnGap: "8px",
  },
});

export const DialogTab = styled(Tab)(({ theme }) => ({
  padding: theme.spacing(0.5, 0.5, 1),
  minHeight: 0,
  fontSize: "1rem",
}));

export const WarningMessage = styled(Typography)(({ theme }) => ({
  fontSize: "0.75rem",
  lineHeight: 16 / 12,
  backgroundColor: theme.palette.shades.orange[50],
  border: "1px solid",
  borderColor: theme.palette.warning.main,
  borderRadius: "4px",
  padding: "6px",
  color: theme.palette.shades.neutral[800],
}));

export const TabContentContainer = styled(Stack)(({ theme }) => ({
  overflowY: "auto",
  [theme.breakpoints.up("sm")]: {
    maxHeight: "400px",
    flexShrink: 1,
  },
  "@media (min-height: 640px)": {
    minHeight: "400px",
    flexGrow: 1,
  },
}));

export const ContentContainer = styled(Stack)(({ theme }) => ({
  flexGrow: 1,
  justifyContent: "stretch",
  padding: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(2, 3),
  },
}));

export const RoleDescription = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  backgroundColor: theme.palette.shades.ghost[300],
  color: theme.palette.shades.neutral[800],
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(2, 5),
  },
}));

type SettingProps = StackProps & {
  label: string;
  description: string;
  wrap?: boolean;
};

export const Setting = styled(
  ({
    label,
    description,
    children,
    spacing = 1,
    wrap: _w,
    ...props
  }: SettingProps) => (
    <Stack spacing={spacing} {...props}>
      <Column spacing="2px">
        <Typography variant="bodyPrimaryMed">{label}</Typography>
        <Typography variant="labelReg" color="shades.neutral.500">
          {description}
        </Typography>
      </Column>
      {children}
    </Stack>
  )
)(({ theme, wrap = true }) => ({
  padding: theme.spacing(1.5, 1),
  color: theme.palette.shades.neutral[800],
  flexDirection: wrap ? "column" : "row",
  flexWrap: wrap ? "wrap" : undefined,
  ...(!wrap && {
    justifyContent: "space-between",
    alignItems: "center",
  }),
  [theme.breakpoints.up("sm")]: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(1.5, 2),
  },
}));

type ParticipantRoleToggleProps = ToggleButtonGroupProps & {
  selected: ParticipantAccessLevel;
};

export const ParticipantRoleToggle = styled(
  ({ selected, ...props }: ParticipantRoleToggleProps) => (
    <ToggleButtonGroup value={selected} exclusive {...props}>
      {ACCESS_LEVEL_OPTIONS.map((option) => (
        <ToggleButton key={option.value} value={option.value}>
          {option.label}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  )
)(({ theme }) => ({
  "& .MuiToggleButton-root": {
    width: "85px",
    flexShrink: 1,
    "&.Mui-selected": {
      color: "#FFF",
      backgroundColor: theme.palette.shades.primary[800],
      "&:hover": {
        backgroundColor: theme.palette.shades.primary[800],
      },
      "&.Mui-disabled": {
        opacity: 0.6,
      },
    },
  },
}));

export const ModalTitle = ({ ...props }: TypographyProps) => {
  return (
    <ClampedTypography
      maxLines={2}
      variant="h2"
      sx={{ color: "shades.neutral.700", lineHeight: "25px", ...props.sx }}
      {...props}
    >
      {props.children}
    </ClampedTypography>
  );
};
